import { useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { ChevronLeft, ChevronRight, X, Wallet, ZoomIn } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { useIsBelowLg } from '@/hooks/use-mobile';

export function ProjectPaymentPlan({ projectId }: { projectId?: string } = {}) {
  const params = useParams<{ id: string }>();
  const id = projectId || params.id;

  const scrollRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsBelowLg();
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const { data: images, isLoading } = useQuery({
    queryKey: ['project-payment-plan-images', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('project_payment_plan_images' as any)
        .select('*')
        .eq('project_id', id!)
        .order('sort_order');
      if (error) throw error;
      return (data || []) as any[];
    },
    enabled: !!id,
  });

  if (isLoading) return <Skeleton className="h-48 rounded-xl" />;
  if (!images || images.length === 0) return null;

  const open = lightboxIdx !== null;

  return (
    <section>
      <div className="flex items-center justify-between mb-3 md:mb-4">
        <h2 className="font-display text-lg md:text-xl font-bold flex items-center gap-2">
          <Wallet className="h-5 w-5 text-primary" />
          Payment Plan
        </h2>
        <span className="text-xs text-muted-foreground">{images.length} {images.length === 1 ? 'document' : 'documents'}</span>
      </div>
      <div className="border rounded-xl bg-card p-3 md:p-4">
        <div className="relative">
          <div
            ref={scrollRef}
            className="flex gap-3 md:gap-4 overflow-x-auto pb-1 snap-x snap-mandatory"
            style={{ scrollbarWidth: 'none' }}
          >
            {images.map((img: any, i: number) => (
              <button
                key={img.id}
                type="button"
                onClick={() => setLightboxIdx(i)}
                className="shrink-0 snap-start group text-left w-[78%] sm:w-[55%] md:w-[340px]"
              >
                <div className="relative overflow-hidden rounded-lg border-2 border-border group-hover:border-primary bg-muted aspect-[4/3] transition-all group-hover:shadow-md">
                  <img
                    src={img.url}
                    alt={img.title || `Payment plan ${i + 1}`}
                    className="absolute inset-0 h-full w-full object-contain bg-background group-hover:scale-[1.02] transition-transform"
                    loading="lazy"
                  />
                  <div className="absolute top-2 right-2 bg-background/90 rounded-full p-1.5 shadow opacity-0 group-hover:opacity-100 transition-opacity">
                    <ZoomIn className="h-3.5 w-3.5 text-primary" />
                  </div>
                  <div className="absolute bottom-2 left-2 text-[10px] font-semibold text-foreground bg-background/90 px-2 py-0.5 rounded shadow-sm">
                    {i + 1} / {images.length}
                  </div>
                </div>
                <p className="mt-2 text-sm font-medium text-foreground line-clamp-2">
                  {img.title || `Payment Plan ${i + 1}`}
                </p>
              </button>
            ))}
          </div>

          {!isMobile && images.length > 1 && (
            <>
              <button
                onClick={() => scrollRef.current?.scrollBy({ left: -360, behavior: 'smooth' })}
                aria-label="Previous"
                className="absolute left-0 top-[42%] -translate-y-1/2 -translate-x-3 bg-card shadow-md rounded-full p-2 hover:bg-muted transition-colors hidden md:flex border"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                onClick={() => scrollRef.current?.scrollBy({ left: 360, behavior: 'smooth' })}
                aria-label="Next"
                className="absolute right-0 top-[42%] -translate-y-1/2 translate-x-3 bg-card shadow-md rounded-full p-2 hover:bg-muted transition-colors hidden md:flex border"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !o && setLightboxIdx(null)}>
        <DialogContent className="max-w-5xl p-0 bg-background border-0 overflow-hidden">
          {open && images[lightboxIdx!] && (
            <div className="relative">
              <button
                onClick={() => setLightboxIdx(null)}
                aria-label="Close"
                className="absolute top-3 right-3 z-10 bg-background/80 hover:bg-background rounded-full p-2 shadow"
              >
                <X className="h-4 w-4" />
              </button>
              <div className="bg-muted/30 flex items-center justify-center max-h-[85vh] p-4">
                <img
                  src={images[lightboxIdx!].url}
                  alt={images[lightboxIdx!].title || `Payment plan ${lightboxIdx! + 1}`}
                  className="max-w-full max-h-[80vh] object-contain"
                />
              </div>
              {images[lightboxIdx!].title && (
                <div className="px-4 py-3 border-t text-sm font-medium">
                  {images[lightboxIdx!].title}
                </div>
              )}
              {images.length > 1 && (
                <>
                  <button
                    onClick={() => setLightboxIdx((i) => (i === null ? null : (i - 1 + images.length) % images.length))}
                    aria-label="Previous image"
                    className="absolute left-3 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background rounded-full p-2 shadow"
                  >
                    <ChevronLeft className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setLightboxIdx((i) => (i === null ? null : (i + 1) % images.length))}
                    aria-label="Next image"
                    className="absolute right-3 top-1/2 -translate-y-1/2 bg-background/80 hover:bg-background rounded-full p-2 shadow"
                  >
                    <ChevronRight className="h-5 w-5" />
                  </button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
